import { useEffect, useState, useRef } from 'react'
import s from './styles.module.scss'
import { Link } from 'react-router-dom'
import { Data8Service } from '../../../services'
import { dialCodes, mainCountryForCode, toInternationalNumber } from '../../../data/dialCodes'
import { DialCodeSelect } from '../dialCodeSelect'
import ClearInputButton from '../../ui/ClearInputButton'

interface inputFieldTextProps {
    fieldName: string
    saveValue: (obj: { field: string; value: string }) => { success: boolean; error: string }
    submitValue?: any
    fieldType?: string | null
    populateValue?: string
    enhanced?: boolean
    required?: boolean
    showErrors?: boolean
    label?: string
    placeholder?: string
    maxLength?: number
    minLength?: number
    className?: string
    showDialCode?: boolean
    // Browser autofill hint, e.g. 'email' or 'tel-national'
    autoComplete?: string
}

export const InputFieldText = (props: inputFieldTextProps) => {
    const { fieldName, saveValue, fieldType, populateValue, enhanced = false, required = false, showErrors = false, label, placeholder, maxLength, minLength, className, showDialCode = false, autoComplete } = props
    const [valueToSave, setValueToSave] = useState('')
    const [error, setError] = useState('')
    const [dialCountry, setDialCountry] = useState('GB')
    const [focused, setFocused] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const validationTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const lastSaved = useRef('')

    useEffect(() => {
        if (typeof populateValue === 'undefined' || populateValue === '' || populateValue === valueToSave) return
        if (!showDialCode) {
            setValueToSave(populateValue)
            return
        }
        // Ignore our own saved "+44 ..." value echoing back, only split numbers populated from elsewhere
        if (populateValue === lastSaved.current) return
        // Longest matching code wins; for shared codes like +1 keep the current country, else the main one
        const rank = (iso2: string, code: string) =>
            iso2 === dialCountry ? 2 : iso2 === mainCountryForCode[code] ? 1 : 0
        const match = dialCodes
            .filter((d) => populateValue.startsWith(`+${d.code}`))
            .sort((a, b) => b.code.length - a.code.length || rank(b.iso2, b.code) - rank(a.iso2, a.code))[0]
        if (match) {
            setDialCountry(match.iso2)
            setValueToSave(populateValue.slice(match.code.length + 1).trim())
        } else {
            setValueToSave(populateValue)
        }
    }, [populateValue])

    useEffect(() => {
        if (valueToSave.trim()) {
            setError((prev) => (prev === 'This field is required' ? '' : prev))
            return
        }
        if (showErrors && required) {
            setError('This field is required')
        } else {
            setError((prev) => (prev === 'This field is required' ? '' : prev))
        }
    }, [showErrors, required, valueToSave])

    const save = (value: string) => {
        lastSaved.current = value
        saveValue({ field: fieldName, value })
    }

    const onChangeFunc = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleValue(e.currentTarget.value, dialCountry)
    }

    const onDialCountryChange = (country: string) => {
        setDialCountry(country)
        if (valueToSave.trim()) handleValue(valueToSave, country)
    }

    const handleValue = (newValue: string, country: string) => {
        setValueToSave(newValue)
        setError('')

        save(showDialCode && newValue.trim() ? toInternationalNumber(country, newValue) : newValue)

        if (validationTimer.current) {
            clearTimeout(validationTimer.current)
        }

        validationTimer.current = setTimeout(async () => {
            try {
                const isValid = await data8Validation(newValue, country)
                if (!isValid) {
                    save('')
                }
            } catch {
                // Data8 unavailable — value already saved above
            }
        }, 300)
    }

    const data8Validation = async (newValue: string, country: string) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

        if (typeof fieldType != 'undefined' && fieldType == 'email') {
            if (newValue.length < 5 || !newValue.includes('@')) {
                setError('Invalid email')
                return false
            }

            if (!newValue.split('@')[1].includes('.')) {
                setError('Invalid email')
                return false
            }

            if (!emailRegex.test(newValue)) {
                setError('Invalid email')
                return false
            }

            const data8Valid = await Data8Service('EmailValidation', 'IsValid', { email: newValue })
            console.log('check email: ', data8Valid)
            if (data8Valid?.Result === 'Invalid') {
                setError('Invalid email')
                return false
            }
        }
        if (typeof fieldType != 'undefined' && fieldType == 'tel') {
            const telPattern = /^[0-9+() \-]+$/
            if (!telPattern.test(newValue)) {
                setError('Phone number contains invalid characters')
                return false
            }
            if (newValue.trim().length < 7) {
                setError('Phone number must be at least 7 characters')
                return false
            }
            if (newValue.length > 20) {
                setError('Phone number must be 20 characters or less')
                return false
            }

            const data8Valid = await Data8Service('PhoneValidation', 'IsValid', {
                telephoneNumber: newValue,
                defaultCountry: showDialCode ? country : 'GB',
            })
            console.log('check tel: ', data8Valid)
            if (data8Valid?.Result?.ValidationResult === 'Invalid') {
                setError('Invalid telephone number')
                return false
            }
        }
        return true
    }

    return (
        <div className={s.fieldWrapper}>
            <div className={`${s.inputWrapper} ${showDialCode ? s.withDialCode : ''}`}>
                {showDialCode && <DialCodeSelect value={dialCountry} onChange={onDialCountryChange} error={!!error} />}
                <div className={s.control}>
                <input
                    ref={inputRef}
                    id={`field-${fieldName}`}
                    className={`${s.inputStyle} ${fieldName == 'registration' ? s.uppercase : ''} ${enhanced ? s.enhanced : ''} ${error ? s.error : ''} ${className || ''}`}
                    type={`${fieldType != null ? fieldType : 'text'}`}
                    name={fieldName}
                    value={valueToSave}
                    onChange={onChangeFunc}
                    autoComplete={autoComplete}
                    required={required}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    minLength={minLength}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                />
                {label && (
                    <label
                        htmlFor={`field-${fieldName}`}
                        className={`${s.label} ${focused || valueToSave !== '' ? s.labelRaised : ''}`}
                    >
                        {label}
                    </label>
                )}
                {valueToSave !== '' && (
                    <ClearInputButton
                        label={label || fieldName}
                        onClear={() => {
                            handleValue('', dialCountry)
                            inputRef.current?.focus()
                        }}
                    />
                )}
                </div>
            </div>
            {error && <span className={s.notice}>{error}</span>}
        </div>
    )
}
